import React, { useState } from "react";
import { Download, CheckCircle2 } from "lucide-react";

const escapeCell = (value) => {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildRow = (cells) => cells.map(escapeCell).join(",");

const buildCSV = (extractedData) => {
  const lines = [];

  const scalarEntries = [];
  const tableEntries  = [];

  Object.entries(extractedData).forEach(([key, value]) => {
    const isTable =
      Array.isArray(value) &&
      value.length > 0 &&
      typeof value[0] === "object" &&
      value[0] !== null;

    if (isTable) {
      tableEntries.push([key, value]);
    } else {
      scalarEntries.push([key, value]);
    }
  });

  // ── Section 1: Scalars ────────────────────────────────────────────────────
  if (scalarEntries.length > 0) {
    lines.push(buildRow(["FIELD", "VALUE"]));
    scalarEntries.forEach(([key, value]) => {
      lines.push(buildRow([key.replace(/_/g, " "), value]));
    });
  }

  // ── Section 2+: Tables ────────────────────────────────────────────────────
  tableEntries.forEach(([key, rows]) => {
    // Blank separator
    lines.push("");
    // Table title row (merged label style)
    lines.push(buildRow([`TABLE: ${key.toUpperCase().replace(/_/g, " ")}`]));

    if (rows.length === 0) {
      lines.push(buildRow(["(empty)"]));
      return;
    }

    // Collect all unique column headers across all rows
    const headers = Array.from(
      new Set(rows.flatMap((row) => Object.keys(row)))
    );

    lines.push(buildRow(headers));

    rows.forEach((row) => {
      const cells = headers.map((h) =>
        row[h] !== undefined && row[h] !== null ? row[h] : ""
      );
      lines.push(buildRow(cells));
    });
  });

  return lines.join("\n");
};

// ─── Component ────────────────────────────────────────────────────────────────

const DownloadCSVButton = ({ data, templateName = "Extraction" }) => {
  const [downloaded, setDownloaded] = useState(false);

  // Extract only extracted_data if full API response is passed
  const extractedData = data?.extracted_data ?? data;

  const isEmpty =
    !extractedData || Object.keys(extractedData).length === 0;

  const handleDownload = () => {
    if (isEmpty) return;

    const csv      = buildCSV(extractedData);
    const blob     = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url      = URL.createObjectURL(blob);
    const date     = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const safeName = templateName.replace(/[^a-z0-9]/gi, "_");
    const filename = `${safeName}_${date}.csv`;

    const link     = document.createElement("a");
    link.href      = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Brief "done" feedback
    setDownloaded(true);
    setTimeout(() => setDownloaded(false), 2000);
  };

  return (
    <button
      className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-semibold border cursor-pointer transition-all duration-150 whitespace-nowrap disabled:opacity-35 disabled:cursor-not-allowed ${
        downloaded
          ? "bg-[#16A34A]/10 border-[#16A34A]/20 text-[#16A34A]"
          : "bg-white border-black/10 text-zinc-500 hover:bg-[#8B55DD] hover:border-[#8B55DD] hover:text-white"
      }`}
      onClick={handleDownload}
      disabled={isEmpty}
      title={isEmpty ? "No data to download" : `Download as CSV`}
    >
      {downloaded ? (
        <>
          <CheckCircle2 size={13} />
          Downloaded!
        </>
      ) : (
        <>
          <Download size={13} />
          Download CSV
        </>
      )}
    </button>
  );
};

export default DownloadCSVButton;